import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { FaTruck, FaPlane } from "react-icons/fa";
import Cookies from "js-cookie";
import { Notification } from "../../../Notification";
import Loader from "../../../Loader";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CourierSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true); // loader state

  useEffect(() => {
    const fetchCouriers = async () => {
      setLoading(true); // start loader
      try {
        const token = Cookies.get("session");
        const response = await axios.get(
          `${REACT_APP_BACKEND_URL}/courier/getCourierServices`,
          {
            headers: {
              authorization: `Bearer ${token}`,
            },
          }
        );

        if (
          response.data &&
          response.data.success &&
          response.data.data &&
          Array.isArray(response.data.data.rateCard)
        ) {
          const updatedCouriers = response.data.data.rateCard.map(
            (courier, index) => ({
              id: index + 1,
              name: courier.courierServiceName || "-",
              provider: courier.courierProviderName || "-",
              courierType: courier.mode || "Domestic (Surface)",
              status: courier.status === "Active" ? "Active" : "Inactive",
            })
          );
          setCouriers(updatedCouriers);
        } else {
          setCouriers([]); // no data
        }
      } catch (error) {
        console.error("Error fetching couriers:", error);
        setCouriers([]);
      } finally {
        setLoading(false); // stop loader
      }
    };

    fetchCouriers();
  }, [location.state]);

  const updateCourierStatus = async (courier) => {
    try {
      const token = Cookies.get("session");
      await axios.post(
        `${REACT_APP_BACKEND_URL}/courier/updateCourierServiceStatus`,
        {
          courierProviderName: courier.provider,
          courierServiceName: courier.name,
          status: courier.status,
        },
        {
          headers: {
            authorization: `Bearer ${token}`,
          },
        }
      );

      Notification(`Status updated successfully for ${courier.name}`, "success");
    } catch (error) {
      console.error(`Error updating status for ${courier.name}:`, error);
      Notification(`Error updating status for ${courier.name}`, "error");
    }
  };

  const toggleStatus = (index) => {
    setCouriers((prevCouriers) =>
      prevCouriers.map((courier, i) => {
        if (i === index) {
          const updatedCourier = {
            ...courier,
            status: courier.status === "Active" ? "Inactive" : "Active",
          };
          updateCourierStatus(updatedCourier);
          return updatedCourier;
        }
        return courier;
      })
    );
  };

  if (loading) {
    return <Loader />; // show loader while fetching
  }

  if (!loading && couriers.length === 0) {
    return (
      <div className="text-center text-sm text-gray-500 mt-4">
        No couriers found.
      </div>
    );
  }

  return (
    <div className="mx-auto sm:mt-2 mt-0">
      {/* Desktop View */}
      <div className="overflow-x-auto hidden md:block">
        <table className="min-w-full">
          <thead>
            <tr className="text-white bg-[#632C9D] border border-[#632C9D] font-[600] text-[12px]">
              <th className="py-2 px-3">Sr.</th>
              <th className="py-2 px-3 text-left">Courier Service</th>
              <th className="py-2 px-3 text-left">Mode</th>
              {/* <th className="py-2 px-3 text-left">Provider</th> */}
              <th className="py-2 px-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {couriers.map((courier, index) => (
              <tr
                key={courier.id}
                className="border border-gray-300 font-[400] text-gray-500 hover:bg-gray-50 transition-all text-[12px]"
              >
                <td className="py-2 px-3 text-center">{index + 1}</td>
                <td className="py-2 px-3">{courier.name}</td>
                <td className="py-2 px-3 text-center">
                  {courier.courierType.includes("Air") ? (
                    <FaPlane className="text-gray-500" />
                  ) : (
                    <FaTruck className="text-gray-500" />
                  )}
                </td>
                {/* <td className="py-2 px-3 text-[#632C9D] underline cursor-pointer">
                  {courier.provider}
                </td> */}
                <td className="py-2 px-3">
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={courier.status === "Active"}
                        onChange={() => toggleStatus(index)}
                      />
                      <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-[#632C9D] relative transition-all duration-300">
                        <div
                          className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${courier.status === "Active" ? "translate-x-5" : "translate-x-1"
                            }`}
                        ></div>
                      </div>
                    </label>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View */}
      <div className="md:hidden flex flex-col gap-2 mt-2">
        {couriers.map((courier, index) => (
          <div
            key={courier.id}
            className="bg-white p-3 rounded-lg shadow border border-gray-200"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">
                {index + 1}. {courier.name}
              </span>
              <div className="text-gray-500">
                {courier.courierType.includes("Air") ? <FaPlane /> : <FaTruck />}
              </div>
            </div>

            <div className="text-xs text-gray-500 mb-1">
              Provider: <span className="text-[#632C9D] underline">{courier.provider}</span>
            </div>

            <div className="flex items-center justify-between mt-2">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={courier.status === "Active"}
                  onChange={() => toggleStatus(index)}
                />
                <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-[#632C9D] relative transition-all duration-300">
                  <div
                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${courier.status === "Active" ? "translate-x-5" : "translate-x-1"
                      }`}
                  ></div>
                </div>
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CourierSelection;
